import {
	type UseMutationOptions,
	useMutation,
	useQueryClient
} from '@tanstack/react-query'
import type {
	Experience,
	ExperienceFlag
} from '@/features/experience-shell/types.ts'
import {
	type HttpEditExperienceSectionFlags,
	httpAddExperienceSectionFlags
} from '@/features/experience-vetting/http/experience-vetting.http.ts'
import { errorHandler } from '@/helpers/utils'
import { QUERY_KEYS } from '@/query-client'

export const useAddExperienceExpertiseFlags = (
	urlParams: Omit<HttpEditExperienceSectionFlags['urlParams'], 'section'>,
	options?: Omit<
		UseMutationOptions<unknown, unknown, { flags: ExperienceFlag[] }>,
		'mutationFn'
	>
) => {
	const queryClient = useQueryClient()
	return useMutation({
		mutationFn: (payload) =>
			httpAddExperienceSectionFlags({
				urlParams: { ...urlParams, section: 'expertises' },
				payload: { flagIds: payload.flags.map((flag) => flag.id) },
				params: {
					response_type: 'small'
				}
			}),
		onError: (error, variables, onMutateResult, context) => {
			queryClient.setQueriesData(
				{
					queryKey: [
						QUERY_KEYS.EXPERIENCE_VETTING,
						urlParams.experienceId
					]
				},
				context
			)
			errorHandler(error)
			options?.onError?.(error, variables, onMutateResult, context)
		},
		onMutate: async (variables) => {
			const queryKey = [
				QUERY_KEYS.EXPERIENCE_VETTING,
				urlParams.experienceId
			]

			// Cancel any outgoing refetches (so they don't overwrite our optimistic update)
			await queryClient.cancelQueries({ queryKey })

			// Snapshot previous values
			const previousData = queryClient.getQueryData<Experience>(queryKey)

			// Update cached data
			if (previousData) {
				queryClient.setQueriesData<Experience>(
					{ queryKey },
					{
						...previousData,
						expertise: {
							...previousData.expertise,
							flags: [
								...previousData.expertise.flags,
								...variables.flags
							]
						}
					}
				)
			}
			return previousData
		},
		...options
	})
}
